import { ChangeDetectorRef, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatToolbar } from '@angular/material/toolbar';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  DeviceService,
  DialogService,
  IconComponent,
  LoadingComponent,
} from '@gains-com/ui-angular';
import { AuthComponent, AuthDialogData } from '../../auth/auth.component.js';
import { AuthService } from '../../core/auth.service.js';
import { UserPreferencesViewService } from '../../core/view-states/user-preferences-view.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { ShellService } from '../shell.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserViewService } from '../../core/view-states/user-view.service';
import { WalletDialogService } from '../../core/wallet-dialog.service.js';
import { MerchDialogService } from '../../core/merch-dialog.service.js';
import { DialogKey } from '../../shared/dialog-keys';
import {
  UserAvatarComponent,
  UserAvatarVM,
} from '../../components/avatar/user-avatar.component';
import { EnvironmentPipe } from '../../pipes/environment.pipe';
import { DecimalPipe } from '../../pipes/decimal.pipe.js';
import { Platform } from '../../core/platform.service.js';
import { NgOptimizedImage } from '@angular/common';
import { MetadataViewService } from '../../core/view-states/metadata-view.service';
import { MerchViewService } from '../../core/view-states/merch-view.service';
import { DeviceModeViewService } from '../../core/view-states/device-mode-view.service';
import { SearchGamesDialogComponent } from '../../components/search-games-dialog/search-games-dialog.component';
import { AuthClickDirective } from '../../directives/auth-click.directive.js';
import { GoldCoinsIconComponent } from '../../components/coins/gold-coins-icon.component.js';
import { SweepsCoinsIconComponent } from '../../components/coins/sweeps-coins-icon.component.js';
import { RegionRestrictedNoticeComponent } from '../../components/region-restricted-notice/region-restricted-notice.component.js';
import { PURCHASE_LIMIT_REACHED_MESSAGE } from '../../components/payments/common.js';

@Component({
  selector: 'app-top-toolbar',
  imports: [
    MatToolbar,
    MatMenuTrigger,
    MatMenuItem,
    MatMenu,
    RouterLink,
    FormsModule,
    MatButton,
    MatIconButton,
    IconComponent,
    UserAvatarComponent,
    LoadingComponent,
    EnvironmentPipe,
    DecimalPipe,
    NgOptimizedImage,
    AuthClickDirective,
    GoldCoinsIconComponent,
    SweepsCoinsIconComponent,
    RegionRestrictedNoticeComponent,
    MatTooltip,
  ],
  templateUrl: './top-toolbar.component.html',
  styleUrl: './top-toolbar.component.scss',
})
export class TopToolbarComponent {
  readonly shell = inject(ShellService);
  readonly device = inject(DeviceService);
  readonly walletDialog = inject(WalletDialogService);
  readonly balanceView = inject(UserPreferencesViewService);
  readonly userView = inject(UserViewService);
  readonly platform = inject(Platform);
  readonly metadata = inject(MetadataViewService);
  readonly merchView = inject(MerchViewService);
  readonly deviceModeView = inject(DeviceModeViewService);

  private readonly _dialog = inject(DialogService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _auth = inject(AuthService);
  private readonly _merchDialog = inject(MerchDialogService);

  readonly purchaseCapMessage = PURCHASE_LIMIT_REACHED_MESSAGE;

  readonly authReady = toSignal(this._auth.ready$);
  readonly user = toSignal(this._auth.user$);
  readonly userState = toSignal(this.userView.state$);
  readonly showWordmark = computed(
    () =>
      this.device.gtSmScreen() ||
      (this.device.deviceKnownOrIsBrowser && !this.user())
  );
  readonly userAvatarVM = computed(() => {
    const user = this.userState();
    return {
      user_id: user?.user_id,
      username: user?.username,
      avatar_url: user?.avatar_url,
      subtitle: user?.email,
    } as UserAvatarVM;
  });
  readonly sweepsCoinsSelected = toSignal(
    this.balanceView.balanceTypeSelected$
  );

  // The device-resolved mode is only this device's once the browser has
  // fetched `/self` itself — SSR resolves it from the render server's egress IP.
  // So the actions area holds a single loading state from first paint through
  // hydration instead of painting the permissive default and flipping through it
  // (dual currency -> loading -> correct mode).
  readonly actionsLoading = computed(
    () =>
      !this.authReady() ||
      (!!this.user() && (!this.userState() || !this.deviceModeView.resolved()))
  );

  openAuthDialog(authView: 'login' | 'signup' = 'login') {
    this._dialog.open<AuthComponent, AuthDialogData>(AuthComponent, {
      id: DialogKey.Auth,
      data: { initialView: authView },
      disableClose: true,
    });
  }

  openSearchDialog() {
    this._dialog.open(SearchGamesDialogComponent);
  }

  goToShop() {
    void this._merchDialog.openShop();
  }

  signOut() {
    this._auth.signOut().then(() => this._cdr.markForCheck());
  }
}
